import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  useTheme,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { selectMainStrategy, selectDetailLoading } from "../../../Redux/Reducers/strategyStoreSlice";
import useStrategyDetail from "./hooks/useStrategyDetail";
import StrategyHeader from "./components/StrategyHeader";
import StrategyMetrics from "./components/StrategyMetrics";
import ChartSection from "./components/ChartSection";
import SimulationPanel from "./components/SimulationPanel";
import StrategyOwner from "./components/StrategyOwner";
import StrategyDetailSkeleton from "./components/StrategyDetailSkeleton";

const StrategyDetail = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [investmentAmount, setInvestmentAmount] = useState("");
  const [simulationEnabled, setSimulationEnabled] = useState(false);
  const [showPercentage, setShowPercentage] = useState(false);

  const { handlePeriodChange, handleCustomFetch } = useStrategyDetail(slug);

  const mainStrategy = useSelector(selectMainStrategy);
  const strategiesLoading = useSelector(selectDetailLoading);

  const parsedAmount = parseFloat(investmentAmount);
  const hasValidAmount = !isNaN(parsedAmount) && parsedAmount > 0;
  const isSimulationActive = simulationEnabled && hasValidAmount;
  const periodReturnDecimal = (mainStrategy?.periodReturn ?? 0) / 100;
  const projectedValue = isSimulationActive ? parsedAmount * (1 + periodReturnDecimal) : null;
  const dollarReturn = isSimulationActive ? parsedAmount * periodReturnDecimal : null;

  if (!mainStrategy) {
    if (strategiesLoading) {
      return (
        <Box sx={{ px: 3, pt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="text"
            onClick={() => navigate("/store")}
            sx={{
              ":hover": {
                transform: "none",
                boxShadow: "none",
                backgroundColor: "none",
              },
            }}
          >
            Back
          </Button>
          <StrategyDetailSkeleton />
        </Box>
      );
    }
    return (
      <Box sx={{ px: 3, pt: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => navigate("/store")}
          sx={{
            ":hover": {
              transform: "none",
              boxShadow: "none",
              backgroundColor: "none",
            },
          }}
        >
          Back to Store
        </Button>
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            p: 3,
            mt: 2,
          }}
        >
          <Typography variant="h4" textAlign={'center'}>
            Strategy not found
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{mainStrategy.title || "Strategy"} | Vantage Alpha</title>
      </Helmet>
      <Box sx={{ px: 3, pt: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => navigate('/store')}
          sx={{
            ":hover": {
              transform: "none",
              boxShadow: "none",
              backgroundColor: "none",
            },
          }}
        >
          Back
        </Button>
      </Box>
      <Grid container spacing={3} sx={{ mt: 1, p: 3 }} alignItems="stretch" direction="row-reverse">
        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex", flexDirection: "column" }}>
          <SimulationPanel
            investmentAmount={investmentAmount}
            onAmountChange={setInvestmentAmount}
            simulationEnabled={simulationEnabled}
            onToggleSimulation={setSimulationEnabled}
            isSimulationActive={isSimulationActive}
            projectedValue={projectedValue}
            dollarReturn={dollarReturn}
          />
          <StrategyOwner />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
            }}
          >
            <StrategyHeader />
            <StrategyMetrics />
            <ChartSection
              showPercentage={showPercentage}
              onTogglePercentage={setShowPercentage}
              investmentAmount={investmentAmount}
              isSimulationActive={isSimulationActive}
              onPeriodChange={handlePeriodChange}
              onCustomFetch={handleCustomFetch}
            />
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default StrategyDetail;
